import type { AppTranslations } from './types'

export const fr = {
  characteristics: {
    common: {
      na: 'N/D',
      no: 'Non',
      source: 'Source',
      sourceCalculated: 'Calculé',
      years: 'ans',
      yes: 'Oui',
    },
    developmentPotential: {
      tooltips: {
        far: {
          calculation: 'Surface brute de plancher totale divisée par la surface du terrain. Exemple : bâtiment de 1000 m² sur un terrain de 500 m² = IUS de 2.0',
          categories: [
            {
              description: 'Rural, maisons individuelles sur grandes parcelles',
              label: 'Faible densité',
              range: 'IUS 0.1-0.5',
            },
            {
              description: 'Suburbain, maisons mitoyennes',
              label: 'Densité moyenne',
              range: 'IUS 0.5-1.5',
            },
            {
              description: 'Urbain, immeubles de taille moyenne',
              label: 'Haute densité',
              range: 'IUS 1.5-3.0',
            },
            {
              description: 'Centre-ville, immeubles de grande hauteur',
              label: 'Très haute densité',
              range: 'IUS >3.0',
            },
          ],
          importance: "L'IUS est l'indicateur principal pour comprendre l'intensité du développement et l'efficacité de l'utilisation du sol. Un IUS plus élevé indique une utilisation plus intensive du terrain et généralement des valeurs immobilières plus élevées par mètre carré de terrain.",
          limitations: "Les réglementations de l'IUS varient selon la zone. Consultez les documents du plan local d'urbanisme pour l'IUS maximal autorisé dans votre zone.",
          shortDescription: 'Rapport entre la surface de plancher totale du bâtiment et la surface de la parcelle',
          source: 'Calculé à partir du Registre fédéral des bâtiments et des logements (RegBL) et du registre foncier',
          title: "Indice d'utilisation du sol (IUS)",
        },
        floorPotential: {
          calculation: "Différence entre le nombre maximal d'étages autorisé (selon le plan local d'urbanisme) et le nombre d'étages actuel",
          importance: "Les étages supplémentaires représentent le moyen le plus direct d'augmenter la capacité du bâtiment et la valeur immobilière. Chaque étage supplémentaire peut ajouter une surface utile significative et un potentiel de revenus locatifs ou de vente.",
          limitations: "L'ajout d'étages nécessite une analyse structurelle, une évaluation de la capacité des fondations, et peut entraîner des exigences en matière d'ascenseurs, de mise aux normes de sécurité incendie et de places de stationnement supplémentaires. Les coûts peuvent être considérables.",
          shortDescription: "Comparaison entre le nombre d'étages actuel et le maximum autorisé par le zonage",
          source: "Réglementations du plan local d'urbanisme (PLU) et Registre fédéral des bâtiments et des logements (RegBL)",
          title: "Potentiel de nombre d'étages",
        },
        heightPotential: {
          calculation: "Différence entre la hauteur maximale autorisée (selon le plan local d'urbanisme) et la hauteur actuelle du bâtiment",
          importance: "Comprendre le potentiel de hauteur est essentiel pour évaluer les possibilités d'extension verticale, la valeur de développement immobilier et la conformité aux réglementations locales. Une hauteur supplémentaire peut augmenter considérablement la surface utile et la valeur du bien.",
          limitations: "Les restrictions de hauteur maximale peuvent être soumises à d'autres facteurs tels que les distances aux limites, les études d'ombrage, les contraintes patrimoniales ou l'opposition du voisinage. Une consultation professionnelle en urbanisme est recommandée.",
          shortDescription: 'Comparaison entre la hauteur actuelle et la hauteur maximale autorisée par le zonage local',
          source: "Réglementations du plan local d'urbanisme (PLU) et Registre fédéral des bâtiments et des logements (RegBL)",
          title: 'Potentiel de hauteur du bâtiment',
        },
      },
    },
    header: {
      subtitle: 'Informations détaillées sur ce bâtiment',
      title: 'Caractéristiques du bâtiment',
    },
    neighborhood: {
      ageRanges: {
        '1919_1945': '1919-1945',
        '1946_1960': '1946-1960',
        '1961_1970': '1961-1970',
        '1971_1980': '1971-1980',
        '1981_1990': '1981-1990',
        '1991_2000': '1991-2000',
        '2001_2010': '2001-2010',
        '2011_2020': '2011-2020',
        post2020: 'Après 2020',
        pre1919: 'Avant 1919',
      },
      badges: {
        buildingOutlier: 'Bâtiment atypique',
      },
      cards: {
        ageDistribution: {
          title: "Distribution de l'âge des bâtiments",
        },
        analysis: {
          tooltip: {
            source: 'RegBL, Popety.io',
          },
        },
        areaComparison: {
          title: 'Comparaison de surface au sol',
          tooltip: {
            calculation: 'Compare la surface au sol de ce bâtiment avec les surfaces moyenne et maximale des bâtiments voisins',
            description: "Comment l'emprise au sol de ce bâtiment se compare aux bâtiments environnants",
            importance: "L'emprise au sol par rapport aux voisins indique l'intensité d'utilisation du sol, la densité de développement et si le bâtiment est typique pour la zone.",
            limitations: "L'emprise au sol seule ne reflète pas la taille totale du bâtiment. Vérifiez le nombre d'étages et la surface brute de plancher pour une évaluation complète.",
            title: 'Comparaison de surface avec les voisins',
          },
        },
        cohesionBreakdown: {
          title: 'Répartition de la cohésion',
          tooltip: {
            calculation: 'Moyenne pondérée des scores de cohésion temporelle (40%), physique (30%), typologique (20%) et de densité (10%)',
            description: "Montre l'homogénéité du quartier selon plusieurs dimensions",
            importance: 'Un score de cohésion élevé indique un quartier bien défini avec des caractéristiques de bâtiment cohérentes',
            title: 'Répartition de la cohésion',
          },
        },
        comparativeAnalysis: {
          title: 'Analyse comparative',
        },
        conformityBreakdown: {
          title: 'Répartition de la conformité du bâtiment',
          tooltip: {
            calculation: 'Compare les caractéristiques de ce bâtiment au profil typique du quartier',
            description: 'Montre à quel point ce bâtiment spécifique correspond au modèle du quartier',
            importance: "Une faible conformité dans un quartier cohérent indique un bâtiment atypique, ce qui peut affecter la valorisation, les restrictions de développement ou l'harmonie architecturale.",
            title: 'Répartition de la conformité du bâtiment',
          },
        },
        heightComparison: {
          title: 'Comparaison de hauteur',
          tooltip: {
            calculation: 'Compare la hauteur totale de ce bâtiment avec les hauteurs moyenne et maximale des bâtiments voisins dans un rayon défini (généralement 100-200m)',
            description: 'Comment la hauteur de ce bâtiment se compare aux bâtiments environnants',
            importance: "La hauteur du bâtiment par rapport aux voisins affecte les vues, l'exposition au soleil, la vie privée, l'harmonie architecturale et la conformité aux règlements de zone locaux.",
            limitations: "Comparaison basée sur les bâtiments dans le rayon de recherche. La topographie locale et les différences d'altitude du terrain ne sont pas reflétées dans cette métrique.",
            title: 'Comparaison de hauteur avec les voisins',
          },
        },
        neighborhoodAnalysis: {
          title: 'Analyse du quartier et du bâtiment',
          tooltip: {
            calculation: 'La cohésion du quartier mesure à quel point les bâtiments environnants sont similaires. La conformité du bâtiment mesure à quel point CE bâtiment correspond au modèle du quartier.',
            description: "Analyse à la fois la cohésion du quartier et dans quelle mesure ce bâtiment s'intègre au quartier détecté",
            importance: 'Une cohésion élevée avec une conformité faible indique un bâtiment atypique dans un quartier cohérent, ce qui peut affecter la valorisation et le potentiel de développement.',
            title: 'Analyse du quartier et du bâtiment',
          },
        },
        quartierProfile: {
          title: 'Profil du quartier',
        },
        volumeComparison: {
          title: 'Comparaison de volume',
          tooltip: {
            calculation: 'Compare le volume total fermé de ce bâtiment avec les volumes moyen et maximal des bâtiments voisins',
            description: 'Comment le volume total de ce bâtiment se compare aux bâtiments environnants',
            importance: "Le volume du bâtiment est un indicateur clé de la masse totale et de l'intensité de développement. Utilisé pour la modélisation énergétique et la planification des capacités CVC.",
            limitations: 'Les calculs de volume peuvent utiliser des hypothèses géométriques simplifiées. Le volume interne réel peut varier en raison de la géométrie du toit et des agencements intérieurs.',
            title: 'Comparaison de volume avec les voisins',
          },
        },
      },
      chart: {
        areaAxis: 'Surface (m²)',
        buildingsAxis: 'Bâtiments',
        heightAxis: 'Hauteur (m)',
        neighborhoodAvg: 'Moy. quartier',
        neighborhoodMax: 'Max. quartier',
        thisBuilding: 'Ce bâtiment',
        volumeAxis: 'Volume (m³)',
      },
      charts: {
        area: {
          source: 'Popety.io, RegBL',
        },
        height: {
          source: 'Popety.io, RegBL',
        },
        volume: {
          source: 'Popety.io, RegBL',
        },
      },
      cohesion: {
        density: {
          label: 'Densité (10%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: 'Basé sur le nombre de bâtiments par kilomètre carré. Une densité plus élevée suggère un quartier urbain.',
            description: 'Mesure la densité de développement',
            title: 'Cohésion de densité',
          },
        },
        physical: {
          label: 'Cohésion physique (30%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: "Basé sur la similarité de hauteur, surface et nombre d'étages. Des scores plus élevés indiquent des caractéristiques de bâtiment uniformes.",
            description: 'Mesure la similarité architecturale',
            title: 'Cohésion physique',
          },
        },
        temporal: {
          label: 'Cohésion temporelle (40%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: 'Basé sur la similarité des années de construction. Des scores plus élevés indiquent des bâtiments de la même époque.',
            description: 'Mesure si les bâtiments ont été construits dans la même période',
            title: 'Cohésion temporelle',
          },
        },
        typological: {
          label: 'Cohésion typologique (20%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: 'Basé sur la prévalence de catégories de bâtiments similaires (ex: maison individuelle, immeuble, etc.)',
            description: 'Mesure la similarité des types de bâtiments',
            title: 'Cohésion typologique',
          },
        },
      },
      comparison: {
        aboveAverage: 'Au-dessus de la moyenne',
        belowAverage: 'En dessous de la moyenne',
        similar: 'Similaire',
      },
      conformity: {
        fitsWell: "S'intègre bien",
        outlier: 'Atypique',
        partialFit: 'Intégration partielle',
        physicalMatch: {
          label: 'Correspondance physique (40%)',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: "Moyenne de la similarité de hauteur, surface et nombre d'étages. ±20-30% = haute correspondance",
            description: 'Dans quelle mesure la taille et la hauteur de ce bâtiment correspondent au quartier typique',
            title: 'Correspondance physique',
          },
        },
        temporalMatch: {
          label: 'Correspondance temporelle (40%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: "Compare l'année du bâtiment à l'année typique. ±5 ans = 100%, ±10 ans = 75%, ±20 ans = 50%",
            description: "Dans quelle mesure l'année de construction de ce bâtiment correspond à la période typique du quartier",
            title: 'Correspondance temporelle',
          },
        },
        tooltip: {
          source: 'Popety.io',
        },
        typologicalMatch: {
          label: 'Correspondance typologique (20%)',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: '100 si même catégorie que typique, 0 si différent',
            description: 'Si le type de ce bâtiment correspond à la catégorie dominante du quartier',
            title: 'Correspondance typologique',
          },
        },
      },
      errors: {
        loadFailed: 'Échec du chargement des données du quartier',
      },
      loading: 'Chargement des données du quartier...',
      metrics: {
        areaVsAverage: {
          label: 'Surface vs moyenne',
          tooltip: {
            calculation: 'Différence en pourcentage par rapport à la surface moyenne des bâtiments dans le rayon de recherche',
            description: "Comment l'emprise de ce bâtiment se compare aux bâtiments voisins",
            importance: 'Indique si le bâtiment est typique ou inhabituellement grand/petit pour la zone. Affecte le COS et le potentiel de développement.',
            source: 'Popety.io, RegBL',
            title: 'Surface vs moyenne du quartier',
          },
        },
        avgNeighborAge: {
          label: 'Âge moyen des voisins',
          tooltip: {
            calculation: 'Moyenne des âges de tous les bâtiments dans le rayon de recherche',
            description: 'Âge moyen des bâtiments environnants',
            importance: 'Indique si le quartier est principalement ancien (>50 ans), établi (20-50 ans) ou nouveau (<20 ans).',
            source: 'Popety.io, RegBL',
            title: 'Âge moyen des voisins',
          },
          value: '{{years}} ans',
        },
        buildingConformity: 'Conformité du bâtiment',
        buildingDensity: {
          label: 'Densité de bâtiments',
          tooltip: {
            calculation: 'Nombre de bâtiments dans un rayon défini divisé par la surface de ce rayon',
            description: 'Nombre de bâtiments par unité de surface dans la zone environnante',
            importance: 'Indique si la zone est urbaine, suburbaine ou rurale. Une densité élevée suggère un environnement urbain avec des bâtiments plus proches.',
            source: 'Popety.io, RegBL',
            title: 'Densité de bâtiments',
          },
          value: '{{density}} bâtiments/km²',
        },
        buildingsAnalyzed: 'Bâtiments analysés',
        confidence: 'Confiance',
        heightVsAverage: {
          label: 'Hauteur vs moyenne',
          tooltip: {
            calculation: 'Différence en pourcentage par rapport à la hauteur moyenne des bâtiments dans le rayon de recherche',
            description: 'Comment la hauteur de ce bâtiment se compare aux bâtiments voisins',
            importance: 'Les bâtiments nettement plus hauts ou plus bas que les voisins peuvent faire face à des restrictions de zone ou se démarquer visuellement.',
            source: 'Popety.io, RegBL',
            title: 'Hauteur vs moyenne du quartier',
          },
        },
        neighborhoodCohesion: 'Cohésion du quartier',
        neighboringBuildings: {
          label: 'Bâtiments voisins',
          tooltip: {
            calculation: 'Nombre de bâtiments trouvés dans le rayon de recherche sélectionné',
            description: "Nombre de bâtiments dans le rayon d'analyse",
            importance: 'Plus de voisins fournissent une meilleure comparaison statistique. Moins de 10 voisins peut indiquer une zone rurale ou une fiabilité des données limitée.',
            source: 'Popety.io, RegBL',
            title: 'Bâtiments voisins',
          },
        },
        volumeVsAverage: {
          label: 'Volume vs moyenne',
          tooltip: {
            calculation: 'Différence en pourcentage par rapport au volume moyen des bâtiments dans le rayon de recherche',
            description: 'Comment le volume de ce bâtiment se compare aux bâtiments voisins',
            importance: 'Les volumes plus grands indiquent des structures plus importantes. Affecte les coûts de chauffage et la densité de développement.',
            source: 'Popety.io, RegBL',
            title: 'Volume vs moyenne du quartier',
          },
        },
      },
      neighboringBuildings: 'Bâtiments voisins',
      noData: 'Aucune donnée de quartier disponible',
      notes: {
        differsByPattern: 'Diffère du modèle du quartier',
        physicalMismatch: 'Les caractéristiques physiques diffèrent de la moyenne du quartier',
        temporalMismatch: "L'âge du bâtiment diffère du modèle du quartier",
        title: 'Notes',
        typologicalMismatch: 'Le type de bâtiment diffère du standard du quartier',
      },
      profile: {
        description: {
          label: 'Description',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: "Généré à partir de l'année de construction typique et de la catégorie dominante",
            description: 'Caractérisation résumée du quartier',
            importance: "Fournit un aperçu rapide du caractère du quartier (ex: 'résidentiel des années 1960' ou 'mixte des années 1980').",
            title: 'Description du quartier',
          },
        },
        developmentCategories: {
          source: 'Popety.io, RegBL',
        },
        developmentPattern: {
          categories: {
            mixed: {
              description: 'Cohésion modérée, développement varié (score 55-74)',
              label: 'Mixte',
            },
            organic: {
              description: 'Faible cohésion, développement diversifié (score <55)',
              label: 'Organique',
            },
            planned: {
              description: 'Haute cohésion, développement uniforme (score ≥75)',
              label: 'Planifié',
            },
          },
          label: 'Modèle de développement',
          source: 'Popety.io, RegBL',
          tooltip: {
            description: 'Classification du style de développement du quartier',
            importance: 'Les zones planifiées ont une zone plus strict. Les zones organiques offrent plus de flexibilité de rénovation.',
            title: 'Modèle de développement',
          },
        },
        typicalArea: {
          label: 'Surface typique',
          source: 'Popety.io',
          tooltip: {
            calculation: 'Surface au sol moyenne des bâtiments du quartier',
            description: 'Emprise au sol représentative pour le quartier',
            importance: "Indique la couverture du lot typique et l'échelle des bâtiments dans la zone.",
            title: 'Surface typique',
          },
        },
        typicalCategory: {
          label: 'Catégorie typique',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: 'Catégorie de bâtiment la plus courante parmi les bâtiments du quartier',
            description: 'Type de bâtiment dominant dans le quartier',
            importance: 'Définit le caractère du quartier : résidentiel, commercial, industriel ou mixte.',
            title: 'Catégorie typique',
          },
        },
        typicalHeight: {
          label: 'Hauteur typique',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Hauteur moyenne des bâtiments du quartier',
            description: 'Hauteur de bâtiment représentative pour le quartier',
            importance: 'Indique si la zone est principalement de faible hauteur (<10m), moyenne hauteur (10-25m) ou grande hauteur (>25m).',
            title: 'Hauteur typique',
          },
        },
        typicalYear: {
          label: 'Année de construction typique',
          source: 'Popety.io, RegBL',
          tooltip: {
            calculation: 'Année de construction moyenne des bâtiments du quartier',
            description: 'Année de construction la plus représentative pour le quartier',
            importance: "Définit l'ère architecturale et les normes de construction probables pour la zone.",
            title: 'Année de construction typique',
          },
        },
      },
      radius: {
        label: "Rayon d'analyse",
        value: '{{value}}m',
      },
      statistics: {
        area: {
          mean: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: 'Moyenne arithmétique de toutes les surfaces de bâtiments',
              description: 'Emprise au sol moyenne dans le quartier',
              importance: 'Indique la taille typique du lot. Influencé par les valeurs aberrantes.',
              title: 'Surface moyenne',
            },
          },
          median: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: '50e percentile des surfaces de bâtiments',
              description: 'Valeur médiane des surfaces de bâtiments du quartier',
              importance: 'Plus représentatif que la moyenne lorsque des valeurs aberrantes existent.',
              title: 'Surface médiane',
            },
          },
          range: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: 'Emprises au sol les plus petites et les plus grandes du quartier',
              description: 'Surfaces minimale et maximale des bâtiments',
              importance: 'Montre la diversité des tailles. Une large étendue indique des échelles de bâtiments mixtes.',
              title: 'Étendue des surfaces',
            },
          },
          title: 'Surface',
        },
        floor: {
          floorsRange: '{{min}} - {{max}} étages',
          floorsValue: '{{floors}} étages',
          mean: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: "Moyenne arithmétique de tous les nombres d'étages",
              description: "Nombre moyen d'étages dans le quartier",
              importance: "Indique l'échelle typique des bâtiments. Influencé par les valeurs aberrantes.",
              title: "Nombre d'étages moyen",
            },
          },
          median: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: "50e percentile des nombres d'étages",
              description: "Valeur médiane des nombres d'étages du quartier",
              importance: 'Plus représentatif que la moyenne lorsque des valeurs aberrantes existent.',
              title: "Nombre d'étages médian",
            },
          },
          range: {
            source: 'Popety.io, RegBL',
            tooltip: {
              calculation: "Nombres d'étages les plus bas et les plus hauts du quartier",
              description: "Nombres d'étages minimum et maximum",
              importance: 'Montre la diversité verticale. Une large étendue indique des niveaux de hauteur mixtes.',
              title: "Étendue du nombre d'étages",
            },
          },
          title: 'Étages',
        },
        height: {
          mean: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Moyenne arithmétique de toutes les hauteurs de bâtiments',
              description: 'Hauteur moyenne des bâtiments dans le quartier',
              importance: "Indique l'échelle typique des bâtiments. Influencé par les valeurs aberrantes.",
              title: 'Hauteur moyenne',
            },
          },
          median: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: '50e percentile des hauteurs de bâtiments',
              description: 'Valeur médiane des hauteurs de bâtiments du quartier',
              importance: 'Plus représentatif que la moyenne lorsque des valeurs aberrantes existent.',
              title: 'Hauteur médiane',
            },
          },
          range: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Hauteurs de bâtiment les plus basses et les plus hautes du quartier',
              description: 'Hauteurs minimale et maximale des bâtiments',
              importance: 'Montre la diversité des hauteurs. Une large étendue indique un développement mixte.',
              title: 'Étendue des hauteurs',
            },
          },
          title: 'Hauteur',
        },
        mean: 'Moyenne',
        median: 'Médiane',
        range: 'Plage',
      },
      title: 'Contexte du quartier',
      tooltips: {
        source: 'Source des données',
        sourceCalculated: 'Calculé à partir des données du registre des bâtiments',
      },
      warnings: {
        outlierDescription: "Ce bâtiment ne correspond pas au modèle cohérent du quartier. Il peut être atypique en termes d'âge, de taille ou de type par rapport aux bâtiments environnants.",
        outlierTitle: 'Ce bâtiment est atypique',
      },
    },
    overview: {
      ageCategories: {
        contemporary: {
          description: 'Construit avec des méthodes et matériaux relativement récents',
          label: 'Contemporain',
          range: '10-30 ans',
        },
        historic: {
          description: 'Bâtiment historique avec potentiellement une valeur patrimoniale et des caractéristiques de caractère',
          label: 'Historique',
          range: '>50 ans',
        },
        mature: {
          description: 'Bâtiment établi pouvant bénéficier de rénovation ou modernisation',
          label: 'Mature',
          range: '30-50 ans',
        },
        new: {
          description: 'Construction récente avec des normes modernes et une efficacité énergétique',
          label: 'Neuf',
          range: '<10 ans',
        },
      },
      cards: {
        dimensions: {
          buildingVolume: 'Volume du bâtiment',
          footprintArea: 'Surface au sol',
          grossFloorArea: 'Surface brute de plancher',
          groundElevation: 'Altitude au sol',
          title: 'Dimensions physiques',
          tooltips: {
            elevation: {
              calculation: 'Mesuré à partir de données LiDAR',
              description: "Hauteur du niveau du sol à l'emplacement du bâtiment au-dessus du niveau de la mer",
              importance: "Important pour l'évaluation du risque d'inondation, la planification du drainage et la compréhension de la topographie locale",
              source: 'Popety.io, swissSURFACE3D',
              title: 'Altitude au sol',
            },
            footprintImportance: 'Détermine la couverture du terrain et est crucial pour calculer le potentiel de développement et la conformité aux réglementations de zone',
            grossFloorArea: {
              calculation: "Somme de toutes les surfaces de plancher y compris les murs, escaliers et espaces communs. Calculé à partir du registre des bâtiments ou surface au sol × nombre d'étages",
              description: 'Surface totale de tous les étages, mesurée à la face extérieure des murs extérieurs',
              importance: "Métrique clé évaluer le potentiel de développement notamment le calcul de l'IBUS",
              limitations: 'Peut inclure des zones non habitables comme les couloirs et locaux techniques. La surface utile nette est généralement 10-25% plus petite',
              source: 'RegBL, Popety.io',
              title: 'Surface brute de plancher',
            },
            heightCalculation: "Hauteur mesurée du niveau du sol au faîte du toit à l'aide de données LiDAR et de relevés officiels",
            heightImportance: 'Crucial pour la conformité à la zone, les relations de voisinage et la détermination de la classification du bâtiment',
            volume: {
              calculation: 'Mesuré à partir de données LiDAR',
              description: 'Volume total fermé du bâtiment des fondations au toit',
              importance: "Utilisé pour la modélisation énergétique, le dimensionnement CVC et les calculs de densité de développement notamment pour l'IM (Indice de Masse)",
              source: 'Popety.io, swissSURFACE3D',
              title: 'Volume du bâtiment',
            },
          },
        },
        occupancy: {
          avgFloorArea: 'Surface moyenne par étage',
          avgFloorHeight: "Hauteur moyenne d'étage",
          buildingCategory: 'Catégorie de bâtiment',
          numberOfDwellings: 'Nombre de logements',
          popetyClassification: 'Classification Popety.io',
          primaryUse: 'Utilisation principale',
          title: 'Occupation & Type',
          tooltips: {
            avgFloorArea: {
              calculation: "Calculé en divisant la surface brute de plancher par le nombre d'étages",
              description: 'La surface moyenne de chaque étage du bâtiment',
              importance: "Indique la surface utile moyenne d'un étage",
              source: 'Popety.io',
              title: "Surface d'étage moyenne",
            },
            avgFloorHeight: {
              calculation: "Calculé en divisant la hauteur totale du bâtiment par le nombre d'étages",
              description: 'La distance verticale moyenne entre les étages du bâtiment',
              importance: 'Indique la hauteur sous plafond typique et le confort vertical du bâtiment',
              source: 'Popety.io',
              title: "Hauteur d'étage moyenne",
            },
            category: {
              calculation: 'Attribué par le RegBL fédéral en fonction de leur usage dominant',
              description: 'Subdivision des bâtiments selon leur affectation',
              importance: 'Classe le bâtiment selon son usage officiel pour le comparer à des bâtiments similaires',
              source: 'RegBL',
              title: 'Catégorie du bâtiment',
            },
            dwellings: {
              calculation: 'Compté à partir des registres officiels des bâtiments',
              description: "Nombre total d'unités résidentielles individuelles dans le bâtiment",
              importance: "Compte les logements du bâtiment, ce qui éclaire l'occupation et l'estimation",
              source: 'RegBL',
              title: 'Nombre de logements',
            },
            popetyClassification: {
              calculation: "Calculé par Popety.io à partir des données du bâtiment, des modèles d'utilisation et de l'analyse du marché",
              description: "Classification du bâtiment déterminée par l'algorithme propriétaire de Popety.io",
              importance: "Fournit une classification standardisée à des fins de comparaison et d'évaluation",
              title: 'Classification Popety.io',
            },
            primaryUse: {
              calculation: "La classification actuelle d'EUROSTAT* subdivise l'ensemble des ouvrages de construction en 2 sections (bâtiment / génie civil), 6 divisions (2/4), 20 groupes (10/10) et 46 classes (21/25).",
              description: "Ventilation des bâtiments selon la classification d'EUROSTAT+",
              importance: "Identifie l'usage dominant, qui oriente le zonage, l'estimation et les comparables",
              source: 'RegBL',
              title: 'Classe de bâtiment',
            },
          },
        },
        occupancyType: {
          title: 'Occupation et type',
        },
        physicalDimensions: {
          title: 'Dimensions physiques',
        },
        status: {
          constructionYear: 'Année de construction',
          currentStatus: 'État actuel',
          lastRenovation: 'Dernière rénovation',
          minergieCertified: 'Certifié Minergie',
          protectedStatus: 'Statut de protection',
          title: 'État & Condition',
          tooltips: {
            constructionYear: {
              calculation: "Reprise de l'année de construction du registre fédéral des bâtiments (RegBL)",
              description: "L'année où le bâtiment a été initialement achevé",
              importance: "Date le bâtiment et sert de base à l'analyse de l'âge, de l'état et des rénovations",
              limitations: 'Pour les bâtiments plus anciens, peut être estimé à partir de la plage de période de construction. Les reconstructions majeures peuvent ne pas mettre à jour cette date',
              source: 'RegBL',
              title: 'Année de construction',
            },
            minergie: {
              calculation: "Attribué par l'association Minergie sur la base du respect de normes strictes d'efficacité énergétique",
              description: "Certification suisse d'efficacité énergétique pour les bâtiments",
              importance: "Indique une efficacité énergétique supérieure, un confort accru et généralement des coûts d'exploitation plus bas",
              source: "Office fédéral de l'énergie OFEN",
              title: 'Certification Minergie',
            },
            protected: {
              calculation: "Évalué par les autorités cantonales du patrimoine en fonction de l'importance historique, architecturale ou culturelle",
              description: 'Niveau de protection du patrimoine attribué au bâtiment par les autorités cantonales',
              importance: 'Affecte les possibilités de rénovation et le potentiel de développement',
              source: 'Office cantonal du patrimoine',
              title: 'Statut de protection',
            },
            renovation: {
              calculation: "Reprise de l'année de la dernière rénovation ayant augmenté la valeur, selon le registre des bâtiments",
              description: 'Année de la dernière rénovation ayant augmenté sensiblement la valeur du bâtiment.',
              importance: 'Indique si le bâtiment a été mis à jour depuis sa construction',
              limitations: 'PLUS MISE À JOUR DANS LE REGBL DEPUIS 2017.',
              source: 'RegBL',
              title: 'Dernière rénovation',
            },
            status: {
              calculation: 'Enregistré dans le registre des bâtiments et mis à jour en fonction des permis de construction ou de démolition',
              description: "L'état opérationnel actuel du bâtiment",
              importance: 'Confirme si le bâtiment existe, est en construction ou a été démoli',
              source: 'RegBL',
              title: 'Statut actuel du bâtiment',
            },
          },
        },
        statusCondition: {
          title: 'Statut et état',
        },
      },
      heightTypes: {
        highRise: 'Élevé',
        lowRise: 'Bas',
        midRise: 'Moyen',
      },
      labels: {
        asl: 'm alt.',
        estimated: 'Estimé',
        period: 'Période',
      },
      metrics: {
        buildingAge: {
          label: 'Âge',
          tooltip: {
            calculation: "Calculé comme l'année actuelle moins l'année de construction du registre officiel des bâtiments",
            calculationEstimated: 'Estimé sur la base de la période de construction {{period}}. Le point médian de la plage de période est utilisé pour le calcul',
            description: "Le nombre d'années depuis la construction originale du bâtiment",
            importance: "Facteur clé pour évaluer les besoins de maintenance, le potentiel de rénovation, l'efficacité énergétique et la valorisation immobilière",
            limitations: "Ne tient pas compte des rénovations majeures ou reconstructions qui peuvent avoir significativement modernisé le bâtiment. Peut être estimé dans le cas ou l'année exacte du bâtiment est inconnue ou une période (e.g: 1946-1960)",
            limitationsEstimated: "L'âge est estimé à partir de la période de construction et peut ne pas être exact. L'année de construction réelle peut varier dans la plage de la période",
            source: 'RegBL, Popety.io',
            title: 'Âge du bâtiment',
          },
        },
        buildingArea: {
          label: 'Surface',
          tooltip: {
            calculation: "Mesuré à partir des données cadastrales officielles ou du registre des bâtiments comme la projection horizontale de l'enveloppe du bâtiment",
            description: 'La surface au niveau du sol du bâtiment',
            importance: "Essentiel pour comprendre l'efficacité de l'utilisation du sol, calculer les ratios de surface de plancher et évaluer le potentiel de développement",
            limitations: "N'inclut pas les structures externes comme les terrasses, balcons ou zones souterraines. Peut différer de la surface totale de plancher",
            source: 'Popety.io, RegBL',
            title: 'Surface au sol du bâtiment',
          },
        },
        buildingHeight: {
          label: 'Hauteur',
          tooltip: {
            calculation: "Mesuré à partir de données LiDAR ou de relevés officiels, du niveau du sol au faîte du toit ou à l'élément structurel le plus haut",
            description: 'La mesure verticale du niveau du sol au point le plus haut du bâtiment',
            importance: 'Crucial pour la conformité à la zone et évaluer le potentiel de développement ou de surélévation',
            limitations: 'Peut varier selon la méthode de mesure. Les antennes, cheminées et structures temporaires peuvent être incluses ou non',
            source: 'Popety.io, swissSURFACE3D',
            title: 'Hauteur totale du bâtiment',
          },
        },
        floorCount: {
          label: 'Étages',
          tooltip: {
            calculation: "Compté à partir des données officielles du registre des bâtiments, les combles et les sous-sols ne sont pris en compte qu'à condition d'être aménagés, même partiellement, pour l'habitation ou chauffés",
            description: "Le nombre total d'étages et de sous-sols du bâtiment, y compris le rez-de-chaussée du bâtiment",
            importance: "Indicateur clé pour la classification des bâtiments, la conformité à la zone et l'estimation de la surface brute de plancher",
            limitations: 'Peut ne pas inclure les niveaux de sous-sol, les mezzanines ou les étages techniques, les caves ne sont pas prises en considération.',
            source: 'RegBL',
            title: "Nombre total d'étages",
          },
        },
        numberOfFloors: {
          label: "Nombre d'étages",
        },
      },
      sizeIndicators: {
        compact: 'Compact',
        large: 'Grand',
        medium: 'Moyen',
      },
      values: {
        asl: 'ASL',
        estimated: '(est.)',
        estimatedFrom: 'Estimé à partir de {{source}}',
        na: 'N/D',
        no: 'Non',
        period: '(période)',
        years: 'ans',
        yearsAgo: 'Il y a {{count}} ans',
        yearsCount: '{{count}} ans',
        yes: 'Oui',
      },
    },
    states: {
      error: 'Erreur: {{error}}',
      loading: 'Chargement...',
      noData: 'Aucune donnée disponible',
    },
    tabs: {
      neighborhood: 'Quartier',
      overview: 'Aperçu',
      timeline: 'Chronologie',
    },
    timeline: {
      ageComparison: {
        comparisonTypes: {
          newerDescription: 'Ce bâtiment a été construit plus récemment que la moyenne du quartier, probablement avec des normes plus modernes',
          newerThanAverage: 'Plus récent que la moyenne',
          olderDescription: "Ce bâtiment est significativement plus ancien que la moyenne du quartier, ce qui peut affecter les besoins de rénovation et l'efficacité énergétique",
          significantlyOlder: 'Significativement plus ancien',
          similarAge: 'Âge similaire',
          similarDescription: 'Ce bâtiment a été construit à peu près en même temps que la plupart des bâtiments du quartier',
        },
        labels: {
          comparison: 'Comparaison',
          neighborhoodAverage: 'Moyenne du quartier',
          thisBuilding: 'Ce bâtiment',
        },
        newerThanAverage: 'Plus récent que la moyenne',
        significantlyOlder: 'Significativement plus ancien',
        similarAge: 'Âge similaire',
        title: "Comparaison d'âge",
        tooltip: {
          calculation: "Basé sur l'année de construction de ce bâtiment comparée à l'année de construction moyenne des bâtiments voisins",
          description: "Compare l'âge de ce bâtiment avec l'âge moyen des bâtiments du quartier environnant",
          importance: "Comprendre comment l'âge d'un bâtiment se compare à son quartier aide à évaluer son état relatif et le contexte de développement potentiel",
          limitations: "La comparaison est basée sur les bâtiments dans le rayon d'analyse. Les zones à développement mixte peuvent montrer une forte variance",
          source: 'RegBL, Popety.io',
          title: "Comparaison d'âge avec le quartier",
        },
        tooltips: {
          comparison: {
            description: "Comment l'âge de ce bâtiment se compare à la moyenne du quartier",
            importance: "Les bâtiments plus récents peuvent avoir une meilleure efficacité énergétique ; les plus anciens peuvent avoir une valeur de caractère ou nécessiter plus d'entretien",
            source: 'Calculé à partir des données du registre des bâtiments',
            title: "Résultat de la comparaison d'âge",
          },
          neighborhoodAverage: {
            calculation: "Année de construction moyenne de tous les bâtiments dans le rayon d'analyse",
            description: "L'année de construction et l'âge moyens des bâtiments du quartier environnant",
            importance: "Indique l'ère de construction typique du quartier, fournissant un contexte pour le bâtiment analysé",
            source: 'RegBL, Popety.io',
            title: 'Moyenne du quartier',
          },
          thisBuilding: {
            calculation: "Année de construction du registre officiel des bâtiments ; âge calculé comme l'année actuelle moins l'année de construction",
            description: "L'année de construction et l'âge du bâtiment analysé",
            importance: "Point de référence pour la comparaison d'âge du quartier",
            source: 'RegBL',
            title: 'Ce bâtiment',
          },
        },
        yearsOld: 'ans',
      },
      badges: {
        estimatedData: 'Données estimées',
        historicalData: 'Données historiques',
      },
      cards: {
        ageAnalysis: {
          title: "Analyse de l'âge",
          tooltips: {
            ageCategory: {
              description: 'Classification du bâtiment en fonction de son âge en catégories standardisées',
              importance: "Place le bâtiment dans une classe d'âge utilisée pour l'état et la comparaison de marché",
              source: 'RegBL, Popety.io',
              title: "Catégorie d'âge",
            },
            buildingAge: {
              calculation: "Calculé comme l'année actuelle moins l'année de construction du registre officiel des bâtiments",
              description: "Le nombre d'années depuis la construction originale du bâtiment",
              importance: "Facteur clé pour évaluer les besoins de maintenance, le potentiel de rénovation, l'efficacité énergétique et la valorisation immobilière",
              limitations: "Ne tient pas compte des rénovations majeures ou reconstructions qui peuvent avoir significativement modernisé le bâtiment. Peut être estimé dans le cas ou l'année exacte du bâtiment est inconnue ou une période (e.g: 1946-1960)",
              source: 'RegBL, Popety.io',
              title: 'Âge du bâtiment',
            },
            buildingPeriod: {
              calculation: "Dérivé du registre des bâtiments lorsqu'une année de construction exacte n'est pas disponible",
              description: "L'ère ou la plage de période de construction du bâtiment",
              importance: 'Fournit un contexte sur les normes de construction et les matériaux typiques de cette époque',
              title: 'Période de construction',
            },
            constructionPeriod: {
              calculation: "Déduite de l'année de construction et des périodes de construction standard",
              source: 'RegBL',
            },
            constructionYear: {
              calculation: "Reprise de l'année de construction officielle du registre des bâtiments",
              importance: "Ancre la chronologie de construction et l'âge du bâtiment",
              limitations: 'Pour les bâtiments plus anciens, peut être estimé à partir de la plage de période de construction. Les reconstructions majeures peuvent ne pas mettre à jour cette date',
              source: 'RegBL',
            },
          },
        },
        ageComparison: {
          title: "Comparaison d'âge avec le quartier",
        },
        constructionPhases: {
          title: 'Phases de construction',
          tooltips: {
            constructionDates: {
              calculation: "Extraites de l'historique de construction du registre des bâtiments",
              description: "Les années spécifiques où les phases de construction ou d'extension ont été achevées",
              importance: "Montre quand les changements majeurs sont survenus, aidant à comprendre l'évolution du bâtiment et les normes de construction mixtes",
              source: 'RegBL',
              title: 'Dates de construction',
            },
            currentStatus: {
              importance: 'Confirme si le bâtiment est opérationnel, en construction ou démoli dans le contexte de son historique de construction',
              source: 'RegBL',
            },
            numberOfPhases: {
              calculation: 'Nombre de dates de construction distinctes enregistrées dans le registre des bâtiments',
              description: "Combien de phases distinctes de construction ou d'extension le bâtiment a subies",
              importance: 'Plusieurs phases peuvent indiquer des extensions ou ajouts affectant la structure et la valeur du bâtiment',
              source: 'RegBL',
              title: 'Nombre de phases de construction',
            },
            singlePhase: {
              calculation: 'Aucune date de construction supplémentaire trouvée dans le registre des bâtiments',
              description: 'Le bâtiment a été construit en une seule phase sans extensions ultérieures',
              importance: 'Les bâtiments en phase unique ont généralement des normes de construction plus uniformes',
              source: 'RegBL',
            },
          },
        },
        constructionTimeline: {
          title: 'Chronologie de construction',
        },
        renovationHistory: {
          title: 'Historique des rénovations',
          tooltips: {
            lastRenovation: {
              calculation: "Reprise de l'année de la dernière rénovation valorisante dans le registre des bâtiments",
              description: 'Année de la dernière rénovation ayant augmenté sensiblement la valeur du bâtiment',
              importance: 'Indique à quand remonte la dernière rénovation ayant augmenté la valeur',
              limitations: 'PLUS MISE À JOUR DANS LE REGBL DEPUIS 2017.',
              source: 'RegBL',
            },
            period: {
              calculation: 'Dérivé des registres de permis de rénovation dans le registre des bâtiments',
              description: "L'ère ou la plage de période durant laquelle la rénovation a été achevée",
              importance: 'Fournit un contexte sur le type et la qualité des travaux de rénovation effectués',
              title: 'Période de rénovation',
            },
            renovationPeriod: {
              calculation: "Déduite de l'année de la dernière rénovation et des périodes de rénovation standard",
              importance: 'Situe la rénovation dans une période utilisée pour comparer des travaux de la même époque',
              limitations: 'PLUS MISE À JOUR DANS LE REGBL DEPUIS 2017.',
              source: 'RegBL',
            },
            renovationStatus: {
              limitations: 'PLUS MISE À JOUR DANS LE REGBL DEPUIS 2017.',
              source: 'Popety.io',
            },
            status: {
              description: 'Évaluation des besoins de rénovation du bâtiment basée sur le temps depuis la dernière rénovation',
              importance: 'Indicateur rapide pour savoir si une rénovation devrait être planifiée dans un futur proche',
              title: 'État de rénovation',
            },
            yearsSince: {
              calculation: "Année actuelle moins l'année de la dernière rénovation du registre des bâtiments",
              description: "Le nombre d'années écoulées depuis la dernière rénovation ayant augmenté sensiblement la valeur du bâtiment",
              importance: "Signale les bâtiments dont la dernière rénovation valorisante n'est plus récente",
              limitations: 'PLUS MISE À JOUR DANS LE REGBL DEPUIS 2017.',
              source: 'RegBL',
              title: 'Années depuis rénovation',
            },
          },
        },
        timeline: {
          title: 'Chronologie de construction',
          tooltip: {
            calculation: 'Basé sur les dates de construction officielles, les registres de rénovation et les données du registre des bâtiments',
            description: "Représentation visuelle de l'historique de construction du bâtiment et des événements majeurs",
            importance: 'Résume les jalons de construction et de rénovation sur une seule chronologie',
            limitations: 'Certaines dates peuvent être estimées à partir des périodes de construction lorsque les années exactes ne sont pas disponibles et certains événements peuvent être manquant',
            source: 'RegBL, Popety.io',
            title: 'Chronologie de construction',
          },
        },
      },
      chart: {
        tooltipAge: 'Âge',
        tooltipYear: 'Année',
        xAxisLabel: 'Année',
        yAxisLabel: 'Bâtiment',
      },
      events: {
        constructionPhase: 'Phase de construction',
        dwellingAdded: 'Logement ajouté',
        majorRenovation: 'Rénovation majeure',
        originalConstruction: 'Construction originale',
        originalConstructionEst: 'Construction originale (Est.)',
        present: 'Présent',
      },
      labels: {
        ageCategory: "Catégorie d'âge",
        buildingAge: 'Âge du bâtiment',
        buildingPeriod: 'Période de construction',
        comparison: 'Comparaison',
        constructionDates: 'Dates de construction',
        constructionYear: 'Année de construction',
        lastRenovation: 'Dernière rénovation',
        neighborhoodAverage: 'Moyenne du quartier',
        noRecord: "Pas d'enregistrement",
        numberOfPhases: 'Nombre de phases',
        phases: 'phases',
        renovationPeriod: 'Période de rénovation',
        renovationStatus: 'État de rénovation',
        singlePhaseConstruction: 'Construction en une phase',
        thisBuilding: 'Ce bâtiment',
        yearsSinceRenovation: 'Années depuis rénovation',
      },
      noData: 'Aucune donnée chronologique disponible',
      noDataMessage: 'Aucune donnée de chronologie de construction disponible',
      phaseLabels: {
        noRecord: 'Aucun enregistrement',
        singlePhase: 'Construction en phase unique',
      },
      renovationCategories: {
        dueSoon: {
          description: 'Le bâtiment pourrait bénéficier de rénovation ou modernisation dans un futur proche',
          label: 'Bientôt nécessaire',
          range: '15-30 ans',
        },
        good: {
          description: 'Rénovation relativement récente ; le bâtiment devrait encore être en bon état',
          label: 'Bon état',
          range: '5-15 ans',
        },
        overdue: {
          description: "Un temps significatif s'est écoulé depuis la dernière rénovation ; des travaux majeurs sont probablement nécessaires",
          label: 'En retard',
          range: '> 30 ans',
        },
        recentlyRenovated: {
          description: 'Bâtiment rénové au cours des 5 dernières années, probablement en excellent état',
          label: 'Récemment rénové',
          range: '< 5 ans',
        },
      },
      renovationStatuses: {
        considerUpdates: 'Mises à jour à considérer',
        goodCondition: 'Bon état',
        noRenovation: 'Pas de rénovation',
        recentlyRenovated: 'Récemment rénové',
        renovationNeeded: 'Rénovation nécessaire',
      },
    },
  },
  gwr: {
    buildingCategory: {
      "Bâtiment à usage partiel d'habitation": "Bâtiment à usage partiel d'habitation",
      "Bâtiment d'habitation avec usage annexe": "Bâtiment d'habitation avec usage annexe",
      "Bâtiment exclusivement à usage d'habitation": "Bâtiment exclusivement à usage d'habitation",
      "Bâtiment partiellement à usage d'habitation": "Bâtiment partiellement à usage d'habitation",
      "Bâtiment sans usage d'habitation": "Bâtiment sans usage d'habitation",
      'Construction particulière': 'Construction particulière',
      'Habitation provisoire': 'Habitation provisoire',
    },
    buildingClass: {
      'Autre bâtiment non classé ailleurs': 'Autre bâtiment non classé ailleurs',
      "Autres bâtiments d'exploitation agricole": "Autres bâtiments d'exploitation agricole",
      "Autres bâtiments d'hébergement de tourisme": "Autres bâtiments d'hébergement de tourisme",
      'Autres bâtiments non classés ailleurs': 'Autres bâtiments non classés ailleurs',
      "Autres bâtiments pour l'hébergement collectif": "Autres bâtiments pour l'hébergement collectif",
      "Autres bâtiments pour l'hébergement de tourisme": "Autres bâtiments pour l'hébergement de tourisme",
      "Bâtiment à usage d'habitation non déterminé": "Bâtiment à usage d'habitation non déterminé",
      'Bâtiment à usage mixte': 'Bâtiment à usage mixte',
      'Bâtiment administratif': 'Bâtiment administratif',
      'Bâtiment commercial': 'Bâtiment commercial',
      "Bâtiment d'exploitation agricole": "Bâtiment d'exploitation agricole",
      'Bâtiment de culte': 'Bâtiment de culte',
      'Bâtiment industriel': 'Bâtiment industriel',
      'Bâtiment pour les transports et les communications': 'Bâtiment pour les transports et les communications',
      'Bâtiment scolaire': 'Bâtiment scolaire',
      'Bâtiments administratifs': 'Bâtiments administratifs',
      'Bâtiments commerciaux': 'Bâtiments commerciaux',
      "Bâtiments d'exploitation agricole": "Bâtiments d'exploitation agricole",
      'Bâtiments de culte': 'Bâtiments de culte',
      'Bâtiments industriels': 'Bâtiments industriels',
      "Bâtiments pour la garde d'animaux": "Bâtiments pour la garde d'animaux",
      'Bâtiments pour les cultures végétales': 'Bâtiments pour les cultures végétales',
      'Bâtiments scolaires': 'Bâtiments scolaires',
      Garage: 'Garage',
      Garages: 'Garages',
      'Gares, aérogares, centraux téléphoniques': 'Gares, aérogares, centraux téléphoniques',
      'Habitat communautaire': 'Habitat communautaire',
      'Hôpital et établissement de santé': 'Hôpital et établissement de santé',
      'Hôpitaux et établissements de santé': 'Hôpitaux et établissements de santé',
      Hôtel: 'Hôtel',
      Hôtels: 'Hôtels',
      'Immeuble à trois logements et plus': 'Immeuble à trois logements et plus',
      'Maison à deux logements': 'Maison à deux logements',
      'Maison individuelle': 'Maison individuelle',
      'Monument historique ou classé': 'Monument historique ou classé',
      'Monuments historiques ou classés': 'Monuments historiques ou classés',
      'Réservoir, silo et entrepôt': 'Réservoir, silo et entrepôt',
      'Réservoirs, silos et entrepôts': 'Réservoirs, silos et entrepôts',
      'Salle de sport': 'Salle de sport',
      'Salle de tir': 'Salle de tir',
      'Salles de sport': 'Salles de sport',
      'Salles de tir': 'Salles de tir',
    },
    buildingStatus: {
      autorisé: 'autorisé',
      démoli: 'démoli',
      'en construction': 'en construction',
      existant: 'existant',
      inutilisable: 'inutilisable',
      'non réalisé': 'non réalisé',
      projeté: 'projeté',
    },
    energySource: {
      Air: 'Air',
      Aucun: 'Aucun',
      Aucune: 'Aucune',
      Autre: 'Autre',
      'Bois (bois déchiqueté, copeaux)': 'Bois (bois déchiqueté, copeaux)',
      'Bois (bûches)': 'Bois (bûches)',
      'Bois (générique)': 'Bois (générique)',
      'Bois (granulés - pellets)': 'Bois (granulés - pellets)',
      'Capteurs solaires thermiques': 'Capteurs solaires thermiques',
      'Chaleur produite à distance (basse température)': 'Chaleur produite à distance (basse température)',
      'Chaleur produite à distance (générique)': 'Chaleur produite à distance (générique)',
      'Chaleur produite à distance (haute température)': 'Chaleur produite à distance (haute température)',
      'Eau (nappe phréatique, eaux superficielles, eaux résiduelles)': 'Eau (nappe phréatique, eaux superficielles, eaux résiduelles)',
      Electricité: 'Electricité',
      Gaz: 'Gaz',
      Indéterminé: 'Indéterminé',
      Mazout: 'Mazout',
      'Pas de générateur': 'Pas de générateur',
      'Pompe à chaleur': 'Pompe à chaleur',
      'Rejets thermiques (dans le bâtiment)': 'Rejets thermiques (dans le bâtiment)',
      'Serpentin géothermique': 'Serpentin géothermique',
      'Soleil (thermique)': 'Soleil (thermique)',
      'Sonde géothermique': 'Sonde géothermique',
    },
    heaterGenerator: {
      Autre: 'Autre',
      'Boiler électrique central': 'Boiler électrique central',
      'Chaudière (générique)': 'Chaudière (générique)',
      'Chaudière (générique) pour plusieurs bâtiments': 'Chaudière (générique) pour plusieurs bâtiments',
      'Chaudière (générique) pour un seul bâtiment': 'Chaudière (générique) pour un seul bâtiment',
      'Chaudière à condensation': 'Chaudière à condensation',
      'Chaudière à condensation pour plusieurs bâtiments': 'Chaudière à condensation pour plusieurs bâtiments',
      'Chaudière à condensation pour un seul bâtiment': 'Chaudière à condensation pour un seul bâtiment',
      'Chaudière standard': 'Chaudière standard',
      'Chaudière standard pour plusieurs bâtiments': 'Chaudière standard pour plusieurs bâtiments',
      'Chaudière standard pour un seul bâtiment': 'Chaudière standard pour un seul bâtiment',
      'Chauffage central électrique': 'Chauffage central électrique',
      'Chauffage central électrique pour plusieurs bâtiments': 'Chauffage central électrique pour plusieurs bâtiments',
      'Chauffage central électrique pour un seul bâtiment': 'Chauffage central électrique pour un seul bâtiment',
      'Chauffage électrique direct (y compris radiateur infra-rouge)': 'Chauffage électrique direct (y compris radiateur infra-rouge)',
      'Échangeur de chaleur (y compris pour la chaleur à distance)': 'Échangeur de chaleur (y compris pour la chaleur à distance)',
      'Echangeur de chaleur (y.c. chauffage à distance)': 'Echangeur de chaleur (y.c. chauffage à distance)',
      'Echangeur de chaleur (y.c. chauffage à distance) pour plusieurs bâtiments': 'Echangeur de chaleur (y.c. chauffage à distance) pour plusieurs bâtiments',
      'Echangeur de chaleur (y.c. chauffage à distance) pour un seul bâtiment': 'Echangeur de chaleur (y.c. chauffage à distance) pour un seul bâtiment',
      Indéterminé: 'Indéterminé',
      'Installation couplage chaleur-force': 'Installation couplage chaleur-force',
      'Installation couplage chaleur-force pour plusieurs bâtiments': 'Installation couplage chaleur-force pour plusieurs bâtiments',
      'Installation couplage chaleur-force pour un seul bâtiment': 'Installation couplage chaleur-force pour un seul bâtiment',
      'Installation solaire thermique': 'Installation solaire thermique',
      'Installation solaire thermique pour plusieurs bâtiments': 'Installation solaire thermique pour plusieurs bâtiments',
      'Installation solaire thermique pour un seul bâtiment': 'Installation solaire thermique pour un seul bâtiment',
      'Pas de générateur de chaleur': 'Pas de générateur de chaleur',
      Poêle: 'Poêle',
      'Pompe à chaleur PAC': 'Pompe à chaleur PAC',
      'Pompe à chaleur PAC pour plusieurs bâtiments': 'Pompe à chaleur PAC pour plusieurs bâtiments',
      'Pompe à chaleur PAC pour un seul bâtiment': 'Pompe à chaleur PAC pour un seul bâtiment',
    },
  },
  ui: {
    exportPdf: {
      unavailable: 'Cet export PDF est indisponible. Fermez l’aperçu et réessayez.',
    },
    aiFilter: {
      analyzing: 'Analyse de votre demande...',
      apply: 'Appliquer',
      clearHistory: 'Effacer',
      description: 'Décrivez ce que vous recherchez en langage naturel et les filtres appropriés seront appliqués.',
      errors: {
        microphoneAccess: "Impossible d'accéder au microphone. Veuillez vérifier vos autorisations.",
        textProcessing: 'Une erreur est survenue lors du traitement de votre demande',
        voiceNotConfigured: "La saisie vocale n'est pas configurée pour ce filtre.",
        voiceProcessing: 'Une erreur est survenue lors du traitement de votre demande vocale',
      },
      examplePrompts: {
        buildings: 'Biens construits après 2020 avec parking',
        properties: 'Montrez-moi les biens de plus de 100m² en centre-ville',
        transactions: 'Trouver les transactions récentes supérieures à 1 million',
      },
      filtersApplied: 'Filtres appliqués',
      filterSaved: 'Filtre enregistré',
      placeholder: 'Saisissez ou dictez votre demande de filtre...',
      recentSearches: 'Recherches récentes',
      recording: 'Enregistrement...',
      saveAsFilter: 'Enregistrer comme filtre',
      startRecording: "Démarrer l'enregistrement vocal",
      stopRecording: "Arrêter l'enregistrement",
      title: 'Assistant de filtre IA',
      trySaying: 'Essayez par exemple :',
    },
    analysisMap: {
      baseLayers: {
        building: 'Bâtiment',
        building3d: 'Bâtiment 3D',
        buildingNumbers: 'Numéros de bâtiment',
        chooseMapStyle: 'Choisir le style de carte',
        contours: 'Courbes de niveau',
        dimensionMetrics: 'Métriques dimensionnelles',
        distanceLimit: 'Distance limite',
        footprint: 'Emprise au sol',
        labels: 'Étiquettes',
        land: 'Terrain',
        mapBase: 'Fond de carte',
        mapFeatures: 'Éléments de carte',
        parcelBoundaries: 'Limites parcellaires',
        pointCloud: 'Nuage de points',
        streets: 'Rues',
        terrain: 'Terrain',
      },
      controls: {
        hideMap: 'Masquer la carte',
      },
      groupLabels: {
        demographicsLayer: 'Couche démographique',
        employmentLayer: 'Couche emploi',
        historicalImagery: 'Imagerie historique',
        noiseLayer: 'Couche bruit',
        purchaseMarketLayer: "Couche marché d'achat",
        rentalMarketLayer: 'Couche marché locatif',
        zoningLayer: 'Couche zonage',
      },
      groupTitles: {
        amenities: 'Commodités',
        buildingPermits: 'Permis de construire',
        demographics: 'Démographie',
        dynamicLayers: 'Couches dynamiques',
        employment: 'Emploi',
        energy: 'Énergie',
        historicalSatelliteImages: 'Images satellites historiques',
        layers: 'Couches',
        naturalHazards: 'Dangers naturels',
        noiseAnalysis: 'Analyse du bruit',
        purchaseListings: 'Annonces de vente',
        purchaseMarket: "Marché d'achat",
        rentalListings: 'Annonces de location',
        rentalMarket: 'Marché locatif',
        restrictions: 'Restrictions',
        sunlightStudy: "Étude d'ensoleillement",
        transactions: 'Transactions',
        transportAccess: 'Transport & Accès',
        zoning: 'Zonage',
        zoningRules: 'Règles de zonage',
      },
      hazardTypes: {
        avalanche: 'Avalanche',
        flood: 'Inondation',
        landslide: 'Glissement de terrain',
        rockslide: 'Éboulement',
        title: 'Types de dangers',
      },
      layers: {
        amenitiesCulture: 'Culture',
        amenitiesCultureDescription: 'Musées, théâtres, lieux culturels',
        amenitiesDining: 'Restauration',
        amenitiesDiningDescription: 'Restaurants, cafés, bars',
        amenitiesEducation: 'Éducation',
        amenitiesEducationDescription: 'Écoles, universités, bibliothèques',
        amenitiesGrocery: 'Épicerie',
        amenitiesGroceryDescription: 'Supermarchés, épiceries',
        amenitiesHealthcare: 'Santé',
        amenitiesHealthcareDescription: 'Hôpitaux, cliniques, pharmacies',
        amenitiesServices: 'Services',
        amenitiesServicesDescription: 'Banques, offices postaux, services publics',
        amenitiesShopping: 'Shopping',
        amenitiesShoppingDescription: 'Centres commerciaux, galeries marchandes, commerces de détail',
        amenitiesTransport: 'Transports',
        amenitiesTransportDescription: 'Arrêts et gares de transports publics',
        amenitiesWellness: 'Bien-être',
        amenitiesWellnessDescription: 'Salles de sport, spas, centres de bien-être',
        buildingPermitsMarkers: 'Permis de construire',
        buildingPermitsMarkersDescription: 'Afficher les emplacements des permis de construire sur la carte',
        cyclingTravelTime: 'Temps de trajet à vélo',
        cyclingTravelTimeDescription: 'Zones cyclables de 5, 10, 15, 30 minutes',
        demographicsAgeGroup: "Groupe d'âge dominant",
        demographicsAgeGroupDescription: 'Segment démographique dominant pour le marketing ciblé',
        demographicsElderlyPct: 'Personnes âgées (65+)',
        demographicsElderlyPctDescription: 'Demande de logements accessibles et de proximité aux soins de santé',
        demographicsForeignPct: 'Population étrangère',
        demographicsForeignPctDescription: 'Indicateur de demande du marché international',
        demographicsGrowthRate: 'Croissance démographique (5 ans)',
        demographicsGrowthRateDescription: "Dynamisme du marché et potentiel d'investissement",
        demographicsHouseholdType: 'Type de ménage dominant',
        demographicsHouseholdTypeDescription: 'Demande en taille de logement (studios vs maisons familiales)',
        demographicsIsochrone: "Zone d'analyse",
        demographicsIsochroneDescription: "Affiche la zone de temps de trajet utilisée pour l'analyse démographique",
        demographicsPopulationDensity: 'Densité de population',
        demographicsPopulationDensityDescription: "Indicateur central d'urbanisation pour la demande immobilière",
        demographicsResidenceDuration: 'Durée de résidence dominante',
        demographicsResidenceDurationDescription: 'Indicateur de stabilité et de rotation du quartier',
        demographicsYoungAdultPct: 'Jeunes adultes (20-39 ans)',
        demographicsYoungAdultPctDescription: 'Dynamisme du marché locatif et demande de premiers logements',
        drivingTravelTime: 'Temps de trajet en voiture',
        drivingTravelTimeDescription: 'Zones de conduite de 5, 10, 15, 30 minutes',
        employmentDensity: "Densité d'emploi",
        employmentDensityDescription: "Indicateur de la demande immobilière commerciale et de l'activité économique",
        employmentDominantSector: "Secteur d'emploi dominant",
        employmentDominantSectorDescription: 'Caractère économique (Primaire/Secondaire/Tertiaire)',
        employmentFulltimeRatio: "Taux d'emploi à plein temps",
        employmentFulltimeRatioDescription: 'Stabilité des revenus et solidité de la demande résidentielle',
        employmentGrowthRate: "Croissance de l'emploi (5 ans)",
        employmentGrowthRateDescription: "Dynamisme du marché et potentiel d'investissement",
        employmentIsochrone: "Zone d'analyse",
        employmentIsochroneDescription: "Affiche la zone de temps de trajet utilisée pour l'analyse de l'emploi",
        employmentJobsPerEstablishment: 'Emplois par établissement',
        employmentJobsPerEstablishmentDescription: 'Indicateur de taille des entreprises (grands groupes vs PME)',
        employmentPublicPrivate: 'Secteur public vs privé',
        employmentPublicPrivateDescription: "Présence de l'État et stabilité des locataires",
        listingsMarkers: "Marqueurs d'annonces",
        listingsMarkersDescription: 'Afficher les annonces immobilières individuelles sur la carte',
        nearbyBuildings: 'Bâtiments voisins',
        noiseRailwayDay: 'Bruit ferroviaire - Jour',
        noiseRailwayDayDescription: 'Niveaux de bruit ferroviaire en journée en dB',
        noiseRailwayNight: 'Bruit ferroviaire - Nuit',
        noiseRailwayNightDescription: 'Niveaux de bruit ferroviaire de nuit en dB',
        noiseRoadDay: 'Bruit routier - Jour',
        noiseRoadDayDescription: 'Niveaux de bruit routier en journée en dB',
        noiseRoadNight: 'Bruit routier - Nuit',
        noiseRoadNightDescription: 'Niveaux de bruit routier de nuit en dB',
        purchaseMarketApartmentPrice: "Prix d'achat moyen/m² - Appartement",
        purchaseMarketApartmentPriceDescription: "Prix d'achat moyen par mètre carré pour les appartements dans la zone",
        purchaseMarketHousePrice: "Prix d'achat moyen/m² - Maison",
        purchaseMarketHousePriceDescription: "Prix d'achat moyen par mètre carré pour les maisons dans la zone",
        rentalMarketApartmentPrice: 'Loyer moyen/m² - Appartement',
        rentalMarketApartmentPriceDescription: 'Loyer moyen par mètre carré pour les appartements dans la zone',
        rentalMarketHousePrice: 'Loyer moyen/m² - Maison',
        rentalMarketHousePriceDescription: 'Loyer moyen par mètre carré pour les maisons dans la zone',
        restrictionsBase: 'Restrictions',
        restrictionsBaseDescription: "Restrictions et contraintes d'utilisation du sol",
        shadowSimulation: "Simulation d'ombres",
        shadowSimulationDescription: "Simulation d'ombres en temps réel basée sur la date et l'heure",
        solarPotential: 'Potentiel solaire',
        solarPotentialDescription: 'Sections de toit colorées par potentiel solaire (kWh/m²/an)',
        sunDirection: 'Direction du soleil',
        sunDirectionDescription: 'Affiche la direction du lever, du coucher et du soleil actuel sous forme de lignes depuis le centre de la carte',
        sunlightHours: "Heures d'ensoleillement",
        sunlightHoursDescription: "Visualisation colorée montrant le nombre total d'heures d'exposition au soleil par jour du lever au coucher du soleil",
        transactionPoints: 'Points de transaction',
        transactionPointsDescription: 'Afficher les transactions immobilières historiques',
        transportBusStops: 'Arrêts de bus',
        transportBusStopsDescription: 'Arrêts de bus publics dans un rayon de 1.5 km',
        transportEvCharging: 'Stations de recharge VE',
        transportEvChargingDescription: 'Stations de recharge pour véhicules électriques dans un rayon de 5 km',
        transportHighwayAccess: 'Accès autoroutier',
        transportHighwayAccessDescription: "Sorties et entrées d'autoroute dans un rayon de 15 km",
        transportTrainStops: 'Gares ferroviaires',
        transportTrainStopsDescription: 'Gares ferroviaires dans un rayon de 1.5 km',
        transportTramStops: 'Arrêts de tram',
        transportTramStopsDescription: 'Arrêts de tram dans un rayon de 1.5 km',
        walkingTravelTime: 'Temps de trajet à pied',
        walkingTravelTimeDescription: 'Zones de marche de 5, 10, 15, 30 minutes',
        zoningAll: 'Tous les types de zones',
        zoningAllDescription: 'Afficher tous les types de zones (Général, Cantonal, Local, Partiel)',
        zoningCantonalOnly: 'Cantonal uniquement',
        zoningCantonalOnlyDescription: 'Afficher uniquement les zones de zonage cantonal',
        zoningGeneralOnly: 'Général uniquement',
        zoningGeneralOnlyDescription: 'Afficher uniquement les zones de zonage général',
        zoningLocalOnly: 'Local uniquement',
        zoningLocalOnlyDescription: 'Afficher uniquement les zones de zonage local',
        zoningMapStyle: 'Carte de zonage',
        zoningMapStyleDescription: 'Afficher le zonage sur la carte standard',
        zoningPartialOnly: 'Partiel uniquement',
        zoningPartialOnlyDescription: 'Afficher uniquement les zones de zonage partiel',
        zoningSatelliteMapStyle: 'Carte de zonage satellite',
        zoningSatelliteMapStyleDescription: "Afficher le zonage sur l'imagerie satellite",
      },
      layersPanel: {
        closePanel: 'Fermer le panneau des couches',
        disabledSelectLayer: 'Désactivé - Sélectionner une couche',
        disableLayer: 'Désactiver la couche',
        hideAll: 'Tout masquer',
        opacity: 'Opacité',
        searchLayers: 'Rechercher des couches',
        searchPlaceholder: 'Rechercher des couches...',
        selectLayer: 'Sélectionner...',
        showAll: 'Tout afficher',
        title: 'Couches de carte',
      },
      legends: {
        cyclingTravelTime: 'Temps de trajet à vélo',
        drivingTravelTime: 'Temps de trajet en voiture',
        min: '{{count}} min',
        walkingTravelTime: 'Temps de trajet à pied',
      },
      mapStyles: {
        light: 'Clair',
        lightDescription: 'Style minimal pour la visualisation de données',
        outdoors: 'Plein air',
        outdoorsDescription: 'Carte plein air avec terrain et sentiers de randonnée',
        popetyio: 'Popety.io',
        popetyioDescription: 'Style de carte Popety.io pour la Suisse',
        satellite: 'Satellite',
        satelliteDescription: 'Imagerie aérienne avec superposition de rues',
        standard: 'Standard',
        standardDescription: 'Style de carte 3D avec bâtiments',
        traffic: 'Trafic',
        trafficDescription: 'Carte avec conditions de trafic',
        zoning: 'Zone',
        zoningDescription: 'Style personnalisé avec couleurs de zone',
        zoningSatellite: 'Zone Satellite',
        zoningSatelliteDescription: 'Imagerie satellite avec superposition de zone',
      },
      restrictionTypes: 'Types de restrictions',
      sunDirectionLegend: {
        currentSun: 'Soleil actuel',
        helpText: 'Les lignes partent du centre de la carte et indiquent la direction du soleil',
        sunrise: 'Lever du soleil',
        sunset: 'Coucher du soleil',
        title: 'Direction du soleil',
      },
      sunlightControls: {
        date: 'Date',
        now: 'Maintenant',
        spring: 'Printemps',
        summer: 'Été',
        sunExposureInfo: "Affichage de l'exposition solaire quotidienne du lever au coucher du soleil",
        time: 'Heure',
        timeline: 'Chronologie du jour',
        today: "Aujourd'hui",
        winter: 'Hiver',
      },
    },
    common: {
      actions: {
        back: 'Retour',
        cancel: 'Annuler',
        close: 'Fermer',
        confirm: 'Confirmer',
        delete: 'Supprimer',
        edit: 'Modifier',
        loadMore: 'Charger plus',
        next: 'Suivant',
        save: 'Enregistrer',
        submit: 'Soumettre',
        viewDetails: 'Voir les détails',
      },
      no: 'Non',
      status: {
        error: "Une erreur s'est produite",
        loading: 'Chargement...',
        saving: 'Enregistrement...',
        success: 'Succès',
      },
      validation: {
        invalidEmail: 'Adresse e-mail invalide',
        invalidFormat: 'Format invalide',
        maxLength: 'Maximum {{count}} caractères',
        minLength: 'Minimum {{count}} caractères',
        required: 'Ce champ est requis',
      },
      yes: 'Oui',
    },
    dataTable: {
      actions: 'Actions',
      bulkActions: {
        exportAsExcel: 'Exporter en Excel',
        itemsSelected: '{{count}} éléments sélectionnés',
      },
      columns: {
        button: 'Colonnes',
        dragHint: 'Glissez les en-têtes pour réorganiser les colonnes',
        moveColumn: 'Déplacer la colonne {{column}}',
        resetToDefault: 'Réinitialiser par défaut',
        showHide: 'Afficher/Masquer les colonnes',
      },
      empty: {
        description: "Essayez d'ajuster vos filtres ou critères de recherche",
        title: 'Aucune donnée trouvée',
      },
      error: {
        description: "Une erreur s'est produite lors du chargement des données",
        title: 'Erreur lors du chargement des données',
      },
      loading: 'Chargement des données...',
      mobile: {
        pullToRefresh: 'Tirer pour rafraîchir',
        refreshing: 'Rafraîchissement...',
        releaseToRefresh: 'Relâcher pour rafraîchir',
        selectColumn: 'Sélectionner une colonne',
        showLess: 'Afficher moins',
        showMore: 'Afficher plus',
        sortAscending: 'Tri croissant',
        sortBy: 'Trier par',
        sortDescending: 'Tri décroissant',
      },
      pagination: {
        items: 'éléments',
        of: 'sur',
        show: 'Afficher',
        showing: 'Affichage de',
      },
    },
    detailPanel: {
      close: 'Fermer',
      closePanel: 'Fermer le panneau',
      viewDetails: 'Voir les détails',
    },
    drawingTools: {
      '3dMode': 'Mode 3D',
      deleteAll: 'Tout supprimer',
      deleteSelected: 'Supprimer la sélection ({{count}})',
      drawLine: 'Dessiner une ligne',
      drawPolygon: 'Dessiner un polygone',
      measureDistance: 'Mesurer la distance',
      tooltip: 'Outils de dessin',
    },
    files: {
      categories: {
        cadFiles: 'Fichiers CAO',
        documents: 'Documents',
        images: 'Images',
        other: 'Autres',
        pdfDocuments: 'Documents PDF',
        spreadsheets: 'Feuilles de calcul',
      },
      confirmDelete: 'Supprimer ce fichier ?',
      deleteFile: 'Supprimer le fichier',
      downloadFile: 'Télécharger le fichier',
      emptyDescription: 'Téléchargez des fichiers pour organiser vos documents importants',
      emptyTitle: 'Aucun fichier téléchargé',
      retry: 'Réessayer',
      searchPlaceholder: 'Rechercher des fichiers...',
      searchResults: '{{count}} résultat sur {{total}} éléments',
      searchResultsPlural: '{{count}} résultats sur {{total}} éléments',
      time: {
        daysAgo: 'Il y a {{count}}j',
        hoursAgo: 'Il y a {{count}}h',
        justNow: "À l'instant",
        lastWeek: 'La semaine dernière',
        minutesAgo: 'Il y a {{count}} min',
        weeksAgo: 'Il y a {{count}} semaines',
        yesterday: 'Hier',
      },
      title: 'Fichiers',
      uploadFile: 'Télécharger un fichier',
      uploadHint: 'PDF, DOC, XLS, DWG, Images (max {{maxSize}} Mo)',
      uploadText: 'Cliquez pour télécharger ou glissez les fichiers ici',
    },
    filters: {
      filterContainer: {
        clearAll: 'Tout effacer',
        hideFilters: 'Masquer les filtres',
        lessFilters: 'Moins de filtres',
        loading: 'Chargement des filtres...',
        moreFilters: 'Plus de filtres',
        resultsFound: '{{count}} résultats trouvés',
        showFilters: 'Afficher les filtres',
      },
      filterPanel: {
        apply: 'Appliquer',
        clear: 'Effacer',
        emptyState: 'Les options de filtre seront affichées ici',
        filters: 'Filtres',
        invalidRange: 'Le minimum doit être inférieur au maximum — les résultats ne sont pas mis à jour',
        reset: 'Réinitialiser',
        resetFilters: 'Réinitialiser les filtres',
        toggleFilters: 'Basculer les filtres',
      },
      quickFilters: {
        activeFilters: 'Filtres actifs',
        removeFilter: 'Supprimer le filtre {{label}}',
        viewLess: 'Voir moins',
        viewMore: '+{{count}} autres',
      },
    },
    forgotPassword: {
      backToLogin: 'Retour à la connexion',
      description: 'Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
      emailLabel: 'Adresse e-mail',
      emailPlaceholder: 'Entrez votre e-mail',
      emailRequired: "L'e-mail est requis",
      errorMessage: "Une erreur s'est produite. Veuillez réessayer.",
      invalidEmail: 'Veuillez entrer une adresse e-mail valide',
      sending: 'Envoi en cours...',
      sendLink: 'Envoyer le lien',
      submitButton: 'Envoyer un nouveau mot de passe',
      success: 'Vérifiez votre e-mail pour un lien de réinitialisation de mot de passe.',
      successMessage: "Si l'e-mail existe, un nouveau mot de passe a été envoyé à votre adresse.",
      title: 'Réinitialiser le mot de passe',
    },
    locationFilter: {
      clear: 'Effacer la localisation',
      noResults: 'Aucune localisation trouvée',
      placeholder: 'Rechercher une localisation...',
      types: {
        address: 'Adresse',
        district: 'District',
        municipality: 'Commune',
        postalCode: 'NPA',
        region: 'Région',
      },
    },
    login: {
      continueWithGoogle: 'Continuer avec Google',
      email: 'E-mail',
      emailPlaceholder: 'Entrez votre e-mail',
      forgotPassword: 'Mot de passe oublié ?',
      noAccount: "Vous n'avez pas de compte ?",
      orContinueWith: 'Ou continuer avec',
      password: 'Mot de passe',
      passwordPlaceholder: 'Entrez votre mot de passe',
      rememberMe: 'Se souvenir de moi',
      signIn: 'Se connecter',
      signingIn: 'Connexion en cours...',
      signUp: "S'inscrire",
      title: 'Connexion',
    },
    map: {
      controls: {
        fullscreen: 'Plein écran',
        myLocation: 'Ma position',
        resetView: 'Réinitialiser la vue',
        zoomIn: 'Zoom avant',
        zoomOut: 'Zoom arrière',
      },
      layers: {
        satellite: 'Satellite',
        streets: 'Rues',
        terrain: 'Terrain',
      },
      vectorTile: {
        error: 'Erreur de chargement des données cartographiques : {{error}}',
        featuresAtLocation: '{{count}} éléments à cet emplacement',
        listingsAtLocation: '{{count}} annonces à cet emplacement',
        loading: 'Chargement des données cartographiques...',
        overlapCount: '{{count}} de plus. Cliquez pour tout voir',
        scrollFeatures: 'Faites défiler pour parcourir les {{count}} éléments',
        scrollListings: 'Faites défiler pour parcourir les {{count}} annonces',
      },
    },
    metricSourceModal: {
      agreement: {
        conflict: 'Conflit — 2 sources sont en désaccord sur cette valeur',
        userModified: "Modifié par l'utilisateur — Valeur originale de {{source}} : {{value}}",
      },
      badges: {
        estimate: 'EST',
        geoportal: 'GEO',
        llm: 'REG',
        manual: 'EDIT',
        old: 'ANC',
      },
      boundSelector: {
        ariaLabel: 'Sélectionner le min ou le max',
        max: 'MAX',
        min: 'MIN',
      },
      estimate: {
        dotStripLabel: 'Répartition de {{metric}} sur {{count}} parcelles — points plus grands = plus de parcelles',
        dotTooltip: '{{count}} parcelle(s) dans cette plage',
        legendEstimate: 'Valeur estimée ({{value}})',
        legendMedian: 'Médiane ({{value}})',
        legendParcel: '1 parcelle',
        legendParcelScale: 'Moins / plus de parcelles',
        loading: 'Chargement des statistiques...',
        noBuildings: 'Aucun bâtiment trouvé dans cette zone pour calculer les statistiques',
        rangeText: 'Les valeurs dans cette zone varient de {{min}} à {{max}} (médiane : {{median}})',
        step1Desc: '{{buildingCount}} bâtiments répartis sur {{landCount}} parcelles ont été utilisés pour le calcul',
        step1Title: 'Nous avons analysé les bâtiments existants dans cette zone',
        step2Desc: {
          cos: "Surface d'emprise au sol des bâtiments divisée par la surface du terrain",
          cus: 'Surface de plancher totale divisée par la surface du terrain',
          floor_nb: "Nombre maximum d'étages par bâtiment sur chaque parcelle",
          height: 'Hauteur maximale des bâtiments sur chaque parcelle',
          ibus: 'Surface brute de plancher divisée par la surface du terrain',
          im: 'Masse du bâtiment (volume) par rapport à la surface du terrain',
          unit_nb: 'Nombre maximum de logements par bâtiment sur chaque parcelle',
          vbr: 'Volume du bâtiment divisé par la surface du terrain',
        },
        step2Title: 'Le {{metric}} a été calculé pour chaque parcelle',
        step3Desc: '{{percentile}}% des parcelles ont un {{metric}} égal ou inférieur à {{value}}',
        step3Title: 'La valeur reflète ce que la plupart des parcelles permettent',
        title: 'Comment cette valeur a-t-elle été calculée ?',
      },
      geoportal: {
        openTab: 'Ouvrir dans un nouvel onglet',
        title: 'Géoportail cantonal',
        viewLink: 'Voir sur le géoportail cantonal',
      },
      labels: {
        confirmedValue: 'Valeur confirmée',
        displayedValue: 'Valeur affichée',
        maximumValue: 'Valeur maximale',
        minimumValue: 'Valeur minimale',
        userValue: 'Valeur utilisateur',
        value: 'Valeur',
      },
      noData: {
        message: 'Aucune valeur disponible pour cette métrique.',
      },
      noDocument: {
        message: 'Aucun document de référence disponible pour cette source.',
      },
      reference: {
        article: 'Article',
        articlePath: "Chemin de l'article",
        document: 'Document',
        documentTitle: 'Titre du document',
        pages: 'Page(s)',
        viewPdf: 'Voir le PDF',
      },
      report: {
        cancel: 'Annuler',
        contextTitle: 'Détails de la valeur',
        error: "Échec de l'envoi du signalement. Veuillez réessayer.",
        explanation: "Qu'est-ce qui est incorrect ?",
        explanationPlaceholder: "Décrivez ce qui est incorrect et l'information correcte...",
        link: 'Signaler une valeur incorrecte',
        submit: 'Envoyer le signalement',
        success: 'Signalement envoyé avec succès. Merci !',
        suggestedValue: 'Valeur correcte (optionnel)',
        suggestedValuePlaceholder: 'Entrez la valeur correcte',
        title: 'Signaler une valeur incorrecte',
      },
      sections: {
        additionalInfo: 'Informations supplémentaires',
        maxAdditionalInfo: 'Valeur maximale - Informations supplémentaires',
        minAdditionalInfo: 'Valeur minimale - Informations supplémentaires',
        referenceInfo: 'Informations de référence',
      },
      sources: {
        estimate: 'Estimé',
        geoportal: 'Géoportail',
        llm: 'Popety.io Research Lab',
        manual: 'Modification manuelle',
        old: 'Données anciennes',
      },
      status: {
        notApplicable: 'Non applicable',
        seeReference: 'Voir référence',
      },
      titleDetails: 'Détails',
      titleRange: 'Plage',
      tooltips: {
        estimate: 'Estimé par les algorithmes Popety.io',
        geoportal: 'Géoportail officiel',
        llm: 'Extrait du règlement',
        manual: "Remplacée par l'utilisateur",
        old: 'Données anciennes',
      },
      userOverride: {
        description: "Cette valeur a été modifiée manuellement par l'utilisateur. Cliquez sur l'onglet {{source}} pour voir les détails de la source originale.",
        noAuthoritativeSource: "Aucune source officielle (règlement ou géoportail) n'était disponible pour cette métrique.",
        timeline: {
          step1Desc: 'Source {{source}} : {{value}}',
          step1NoSource: 'Aucune source originale disponible',
          step1Title: "Valeur estimée par l'algorithme",
          step1TitleGeo: 'Valeur provenant du géoportail',
          step1TitleReg: 'Valeur provenant du règlement',
          step2Desc: 'La valeur {{source}} ({{originalValue}}) a été remplacée manuellement',
          step2DescNoSource: "Une valeur manuelle a été définie par l'utilisateur",
          step2Title: "L'utilisateur a modifié la valeur à {{value}}",
          step3Desc: "Différence : {{diff}} par rapport à l'estimation",
          step3DescNoOriginal: 'Valeur définie manuellement',
          step3Title: 'La valeur utilisée est {{value}}',
          title: 'Comment cette valeur a-t-elle été définie ?',
        },
      },
      warnings: {
        estimate: "Cette valeur a été estimée à l'aide de notre modèle interne basé sur des propriétés similaires dans la région. Veuillez noter qu'il s'agit d'une estimation qui peut ne pas refléter la valeur autorisée réelle. Pour des informations plus précises, veuillez consulter les documents de zone officiels ou contacter les autorités locales.",
        estimateShort: "Cette valeur a été estimée par les algorithmes de Popety.io à l'aide d'une analyse spatiale des parcelles de la même zone. Elle peut différer des documents de zonage officiels. Vérifiez toujours auprès des autorités locales.",
        geoportalShort: 'Cette valeur provient du géoportail cantonal officiel. Des écarts peuvent survenir si la réglementation a récemment changé. Veuillez vérifier auprès des documents de zone officiels ou des autorités locales.',
        llm: "Cette valeur a été recherchée et fournie par le laboratoire de recherche de Popety.io à l'aide d'algorithmes avancés et de travail manuel. Bien que nous nous efforcions d'être précis, veuillez vérifier ces informations auprès des documents de zone officiels ou des autorités locales pour garantir la conformité.",
        regulationShort: "Cette valeur a été recherchée et fournie par le laboratoire de recherche de Popety.io à l'aide d'algorithmes avancés et de travail manuel. Bien que nous nous efforcions d'être précis, veuillez vérifier auprès des documents de zone officiels ou des autorités locales.",
      },
    },
    pdfViewer: {
      errors: {
        defaultMessage: "Une erreur s'est produite lors du chargement du document PDF. Veuillez réessayer.",
        loadFailed: 'Échec du chargement du PDF',
        noUrl: 'Aucune URL de PDF fournie',
        pageNotFound: 'Page non trouvée',
        retry: 'Réessayer',
        tryAgain: 'Réessayer',
      },
      loading: {
        text: 'Chargement du document PDF...',
      },
      search: {
        closeSearch: 'Fermer la recherche (Échap)',
        nextMatch: 'Résultat suivant (Entrée)',
        noMatches: 'Aucun résultat',
        noResults: 'Aucun résultat trouvé',
        previousMatch: 'Résultat précédent (Maj+Entrée)',
        resultsCount: '{{current}} sur {{total}}',
      },
      toolbar: {
        download: 'Télécharger',
        downloadPdf: 'Télécharger le PDF',
        exitFullscreen: 'Quitter le plein écran',
        fullscreen: 'Plein écran',
        nextPage: 'Page suivante',
        of: 'sur',
        page: 'Page',
        previousPage: 'Page précédente',
        resetZoom: 'Réinitialiser le zoom',
        rotateLeft: 'Rotation gauche',
        rotateRight: 'Rotation droite',
        search: 'Rechercher',
        searchPlaceholder: 'Rechercher dans le document...',
        searchShortcut: 'Rechercher (Ctrl+F)',
        zoomIn: 'Zoom avant',
        zoomOut: 'Zoom arrière',
      },
    },
    propertyAttributes: {
      categories: {
        amenities: 'Commodités & Caractéristiques',
        basic: 'Informations de base',
        condition: 'État & Qualité',
        energy: 'Énergie & Systèmes',
        environment: 'Environnement & Emplacement',
        physical: 'Caractéristiques physiques',
      },
      empty: 'Aucun attribut de propriété disponible.',
      fields: {
        airConditioning: 'Climatisation',
        altitude: 'Altitude',
        attic: 'Grenier',
        balcony: 'Surface du balcon',
        buildingHeight: 'Hauteur du bâtiment',
        cellar: 'Cave',
        condition: 'État général',
        constructionYear: 'Année de construction',
        distanceToPowerlines: 'Distance aux lignes électriques',
        elevator: 'Ascenseur',
        energyClass: 'Classe énergétique',
        exposureOrientation: "Orientation de l'exposition",
        facadeCondition: 'État de la façade',
        fireplace: 'Cheminée',
        floor: 'Étage',
        floorArea: 'Surface au sol',
        garage: 'Places de garage',
        garden: 'Surface du jardin',
        heatConsumptionIndex: 'Indice de consommation de chaleur',
        heatingSystemAge: 'Âge du système de chauffage',
        heatingType: 'Type de chauffage',
        lakeView: 'Vue sur le lac',
        landArea: 'Surface du terrain',
        lastMaintenanceYear: 'Dernière année de maintenance',
        livingArea: 'Surface habitable',
        minergie: 'Certifié Minergie',
        mountainView: 'Vue sur les montagnes',
        noiseLevel: 'Niveau de bruit',
        numberOfBathrooms: 'Salles de bain',
        numberOfBedrooms: 'Chambres',
        numberOfFloors: "Nombre d'étages",
        parking: 'Places de parking',
        pool: 'Piscine',
        propertyType: 'Type de propriété',
        publicTransportQuality: 'Qualité des transports publics',
        renovationYear: 'Année de rénovation',
        roofCondition: 'État du toit',
        roomsNb: 'Nombre de pièces',
        sauna: 'Sauna',
        slope: 'Pente',
        solarArea: 'Surface des panneaux solaires',
        solarPanelsInstalled: 'Panneaux solaires installés',
        solarPotential: 'Potentiel solaire',
        terrace: 'Surface de la terrasse',
        travelTimeToCenters: 'Temps de trajet vers les centres',
        usableArea: 'Surface utile',
        volume: 'Volume',
        windowsCondition: 'État des fenêtres',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        rooms: 'pièces',
        spaces: 'places',
        squareMeters: 'm²',
        years: 'ans',
      },
    },
    propertyAttributesForm: {
      buttons: {
        cancel: 'Annuler',
        save: 'Enregistrer les modifications',
        saving: 'Enregistrement...',
      },
      categories: {
        amenities: 'Commodités & Caractéristiques',
        basic: 'Informations de base',
        condition: 'État & Qualité',
        energy: 'Énergie & Systèmes',
        environment: 'Environnement & Emplacement',
        physical: 'Caractéristiques physiques',
      },
      fields: {
        airConditioning: 'Climatisation',
        altitude: 'Altitude',
        attic: 'Grenier',
        balcony: 'Surface du balcon',
        buildingHeight: 'Hauteur du bâtiment',
        cellar: 'Cave',
        condition: 'État général',
        constructionYear: 'Année de construction',
        distanceToPowerlines: 'Distance aux lignes électriques',
        elevator: 'Ascenseur',
        energyClass: 'Classe énergétique',
        exposureOrientation: "Orientation de l'exposition",
        facadeCondition: 'État de la façade',
        fireplace: 'Cheminée',
        floor: 'Étage',
        floorArea: 'Surface au sol',
        garage: 'Places de garage',
        garden: 'Surface du jardin',
        heatConsumptionIndex: 'Indice de consommation de chaleur',
        heatingSystemAge: 'Âge du système de chauffage',
        heatingType: 'Type de chauffage',
        lakeView: 'Qualité de la vue sur le lac',
        landArea: 'Surface du terrain',
        lastMaintenanceYear: 'Dernière année de maintenance',
        livingArea: 'Surface habitable',
        minergie: 'Certifié Minergie',
        mountainView: 'Qualité de la vue sur les montagnes',
        noiseLevel: 'Niveau de bruit',
        numberOfBathrooms: 'Nombre de salles de bain',
        numberOfBedrooms: 'Nombre de chambres',
        numberOfFloors: "Nombre d'étages",
        parking: 'Places de parking',
        pool: 'Piscine',
        propertyType: 'Type de propriété',
        publicTransportQuality: 'Qualité des transports publics',
        renovationYear: 'Année de rénovation',
        roofCondition: 'État du toit',
        roomsNb: 'Nombre de pièces',
        sauna: 'Sauna',
        slope: 'Pente du terrain',
        solarArea: 'Surface des panneaux solaires',
        solarPanelsInstalled: 'Panneaux solaires installés',
        solarPotential: 'Potentiel solaire',
        terrace: 'Surface de la terrasse',
        travelTimeToCenters: 'Temps de trajet vers les centres',
        usableArea: 'Surface utile',
        volume: 'Volume',
        windowsCondition: 'État des fenêtres',
      },
      options: {
        heatingType: {
          districtHeating: 'Chauffage à distance',
          electricity: 'Électricité',
          gas: 'Gaz',
          heatPump: 'Pompe à chaleur',
          oil: 'Mazout',
          other: 'Autre',
          pellets: 'Pellets',
          solarThermal: 'Solaire thermique',
          wood: 'Bois',
        },
        level: {
          high: 'Élevé',
          low: 'Faible',
          medium: 'Moyen',
        },
        quality: {
          bad: 'Mauvais',
          good: 'Bon',
          medium: 'Moyen',
        },
        select: 'Sélectionner...',
        viewQuality: {
          excellent: 'Excellente',
          good: 'Bonne',
          none: 'Aucune',
          partial: 'Partielle',
        },
      },
      placeholders: {
        altitude: 'p. ex., 450',
        balcony: 'p. ex., 12',
        buildingHeight: 'p. ex., 12',
        condition: 'p. ex., excellent, bon, moyen',
        constructionYear: 'p. ex., 1985',
        distanceToPowerlines: 'p. ex., 200',
        exposureOrientation: 'p. ex., sud, sud-ouest',
        facadeCondition: 'p. ex., excellent, bon, moyen',
        floor: 'p. ex., 3',
        floorArea: 'p. ex., 100',
        garage: 'p. ex., 1',
        garden: 'p. ex., 200',
        heatConsumptionIndex: 'p. ex., 80',
        heatingSystemAge: 'p. ex., 10',
        landArea: 'p. ex., 500',
        lastMaintenanceYear: 'p. ex., 2020',
        livingArea: 'p. ex., 120',
        numberOfBathrooms: 'p. ex., 2',
        numberOfBedrooms: 'p. ex., 3',
        numberOfFloors: 'p. ex., 4',
        parking: 'p. ex., 2',
        propertyType: 'p. ex., appartement, maison, villa',
        renovationYear: 'p. ex., 2015',
        roofCondition: 'p. ex., neuf, bon, à réparer',
        roomsNb: 'p. ex., 4.5',
        slope: 'p. ex., plat, légère, forte',
        solarArea: 'p. ex., 30',
        terrace: 'p. ex., 25',
        travelTimeToCenters: 'p. ex., 15 minutes du centre-ville',
        usableArea: 'p. ex., 140',
        volume: 'p. ex., 450',
        windowsCondition: 'p. ex., double vitrage, neuf',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        squareMeters: 'm²',
        years: 'ans',
      },
    },
    propertyDescriptionEditor: {
      buttons: {
        cancel: 'Annuler',
        edit: 'Modifier',
        editWithAi: "Modifier avec l'IA",
        save: 'Enregistrer',
        saving: 'Enregistrement...',
      },
      characters: 'caractères',
      emptyState: 'Cliquez sur "Modifier avec l\'IA" pour générer rapidement une description bien structurée et remplir automatiquement les attributs de la propriété, ou cliquez sur "Modifier" pour en ajouter une manuellement.',
      recommended: 'Recommandé : {{min}}-{{max}} caractères',
      title: 'Description de la propriété',
    },
    propertyDetails: {
      attributes: {
        empty: "Aucun attribut de propriété n'a encore été ajouté.",
        title: 'Attributs',
      },
      buttons: {
        edit: 'Modifier',
        editWithAI: "Modifier avec l'IA",
        generateWithAI: "Générer avec l'IA",
        generating: 'Génération...',
      },
      description: {
        empty: "Aucune description n'a encore été ajoutée.",
        title: 'Description de la propriété',
      },
      situationLocation: {
        empty: "Aucune description de situation et d'emplacement n'a encore été ajoutée.",
        title: 'Situation & Emplacement',
      },
      subtitle: 'Description détaillée et informations sur la propriété.',
      title: 'Détails de la propriété',
    },
    reportModal: {
      cancel: 'Annuler',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce rapport ?',
      createNew: 'Créer un nouveau rapport',
      delete: 'Supprimer',
      edit: 'Modifier',
      noReports: 'Aucun rapport créé',
      sharedWithOffice: 'Partagé avec le bureau',
      title: 'Rapports PDF',
      typeBuildingAnalysis: 'Analyse du bâtiment',
      typeEstimate: 'Estimation',
      typeLandAnalysis: 'Analyse foncière',
      typeLandGroupingAnalysis: 'Analyse de regroupement de parcelles',
      untitledReport: 'Rapport sans titre',
      updated: 'Mis à jour',
    },
    savedItems: {
      buildingPermits: {
        empty: 'Aucun permis de construire sauvegardé',
        remove: 'Retirer des sauvegardés',
        title: 'Permis de construire sauvegardés',
      },
      listings: {
        empty: 'Aucune annonce sauvegardée',
        remove: 'Retirer des sauvegardés',
        title: 'Annonces sauvegardées',
      },
      transactions: {
        empty: 'Aucune transaction sauvegardée',
        remove: 'Retirer des sauvegardés',
        title: 'Transactions sauvegardées',
      },
    },
    tags: {
      addExistingTag: 'Ajouter un tag existant',
      addSelectedTag: 'Ajouter le tag sélectionné',
      addTags: 'Ajouter des tags',
      cancel: 'Annuler',
      createAndAdd: 'Créer et ajouter',
      createNewTag: 'Créer un nouveau tag',
      currentTags: 'Tags actuels',
      manageTitle: 'Gérer les tags',
      noAvailableTags: 'Aucun tag disponible',
      noMatchingTags: 'Aucun tag correspondant',
      noTagsYet: 'Aucun tag assigné',
      primary: 'Principal',
      primaryConfirm: 'Le tag principal existant sera changé en secondaire. Continuer ?',
      primaryWarning: 'Le tag principal existant deviendra secondaire',
      searchPlaceholder: 'Rechercher des tags...',
      secondary: 'Secondaire',
      setPrimary: 'Définir comme tag principal',
      tagNamePlaceholder: 'Nom du tag...',
    },
    tooltipMetric: {
      categories: 'Catégories :',
      howCalculated: "Comment c'est calculé :",
      whatItMeans: 'Ce que cela signifie :',
      whyItMatters: "Pourquoi c'est important :",
    },
  },
} satisfies AppTranslations
